import React, { useMemo } from 'react';
import type { TCredentialingContract } from 'types/credentialing';
import type { SettingEnum } from 'types/enumerationData';
import { getStateDisplayName } from 'utils/common';
import { SvgInfoCircle } from '../SvgIcons';
import s from './s.module.less';

type Props = {
    verifyingContracts?: TCredentialingContract[];
    countersignedContracts: TCredentialingContract[];
    fullySignedContracts: TCredentialingContract[];
    stateList?: SettingEnum[];
    onViewContracts: () => void;
};

const CountersignedBanner = ({ verifyingContracts = [], countersignedContracts, fullySignedContracts, stateList, onViewContracts }: Props) => {
    const verifyingText = useMemo(() => {
        if (verifyingContracts.length === 0) return '';
        const payers = [...new Set(verifyingContracts.map((c) => c.payerName))];
        if (payers.length === 1) {
            return `We are verifying the signature status of your ${payers[0]} contract.`;
        }
        return `We are verifying the signature status of your ${payers.join(' and ')} contracts.`;
    }, [verifyingContracts]);

    const countersignedText = useMemo(() => {
        if (countersignedContracts.length === 0) return '';
        const payers = [...new Set(countersignedContracts.map((c) => c.payerName))];
        if (payers.length === 1) {
            return `You signed the ${payers[0]} contract. ${payers[0]} is countersigning it now; the fully signed copy will be saved under Contracts in Settings.`;
        }
        return `You signed ${payers.length} contracts. ${payers.join(', ')} are countersigning them now; the fully signed copies will be saved under Contracts in Settings.`;
    }, [countersignedContracts]);

    const fullySignedText = useMemo(() => {
        if (fullySignedContracts.length === 0) return '';
        const payerStateMap = new Map<string, string[]>();
        fullySignedContracts.forEach((c) => {
            const states = payerStateMap.get(c.payerName) || [];
            const name = getStateDisplayName(c.payerState, stateList) || c.payerState;
            if (!states.includes(name)) states.push(name);
            payerStateMap.set(c.payerName, states);
        });
        const labels = [...payerStateMap.entries()].map(
            ([payer, states]) => `${payer} (${states.join(', ')})`,
        );
        const plural = fullySignedContracts.length > 1;
        const payerLabel = `${labels.join(' and ')} ${plural ? 'contracts are' : 'contract is'}`;
        return `Your ${payerLabel} fully signed and saved under Contracts. Each payer activates your participation next and sends the PIN and effective date by email; the panel shows Approved when that arrives.`;
    }, [fullySignedContracts, stateList]);

    if (!verifyingText && !countersignedText && !fullySignedText) return null;

    return (
        <>
            {verifyingText && (
                <div className={s.infoBanner}>
                    <div className={s.bannerIcon}><SvgInfoCircle color="#2563EB" /></div>
                    <span className={s.bannerText}>{verifyingText}</span>
                    <span className={s.bannerAction} onClick={onViewContracts}>
                        View contracts &rarr;
                    </span>
                </div>
            )}
            {countersignedText && (
                <div className={s.infoBanner}>
                    <div className={s.bannerIcon}><SvgInfoCircle color="#2563EB" /></div>
                    <span className={s.bannerText}>{countersignedText}</span>
                    <span className={s.bannerAction} onClick={onViewContracts}>
                        View contracts &rarr;
                    </span>
                </div>
            )}
            {fullySignedText && (
                <div className={s.infoBanner}>
                    <div className={s.bannerIcon}><SvgInfoCircle color="#2563EB" /></div>
                    <span className={s.bannerText}>{fullySignedText}</span>
                    <span className={s.bannerAction} onClick={onViewContracts}>
                        View contracts &rarr;
                    </span>
                </div>
            )}
        </>
    );
};

export default CountersignedBanner;
